// ./01 - PROJETOS DEV/meu-controle-de-estoque/src/components/views/CompromissosView.jsx
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import Pagination from '../Pagination'; 
import { formatCurrency } from '../../utils/formatters';

const months = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

const CompromissosView = ({ 
    purchaseFlow, 
    openModal, 
    purchases, 
    bulkAddPaymentsToPurchases, 
    scrollTo, 
    onScrollComplete,
    selectedIds = [], 
    onSelectOne, 
    onSelectAll, 
    onDeleteSelected 
}) => {
    const tableRef = useRef(null);

    const [payablesFilters, setPayablesFilters] = useState({ search: '', year: 'all', month: 'all' });
    const [payablesPage, setPayablesPage] = useState(1);
    const [payablesItemsPerPage, setPayablesItemsPerPage] = useState(5);
    const [payablesSort, setPayablesSort] = useState({ key: 'dueDate', direction: 'asc' });

    const [historyFilters, setHistoryFilters] = useState({ search: '', year: 'all', month: 'all' });
    const [historyPage, setHistoryPage] = useState(1);
    const [historyItemsPerPage, setHistoryItemsPerPage] = useState(5);
    const [historySort, setHistorySort] = useState({ key: 'date', direction: 'desc' });

    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [selectedMetric, setSelectedMetric] = useState('monthlyToPay');

    const [selectedPayables, setSelectedPayables] = useState(new Set());
    const [editedBulkAmounts, setEditedBulkAmounts] = useState({}); 
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [bulkPaymentDate, setBulkPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [bulkPaymentMethod, setBulkPaymentMethod] = useState('Pix');

    useEffect(() => {
        if (scrollTo === 'contas-a-pagar') {
            tableRef.current?.scrollIntoView({ behavior: 'smooth' });
            onScrollComplete?.(); 
        }
    }, [scrollTo, onScrollComplete]);

    const handlePayablesSort = useCallback((key) => {
        setPayablesSort(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
    }, []);

    const handleHistorySort = useCallback((key) => {
        setHistorySort(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
    }, []);

    const handlePayablesFilterChange = (e) => {
        const { name, value } = e.target;
        setPayablesFilters(prev => {
            const newFilters = { ...prev, [name]: value };
            if (name === 'year') newFilters.month = 'all';
            return newFilters;
        });
        setSelectedPayables(new Set());
        setEditedBulkAmounts({});
    };

    const handleHistoryFilterChange = (e) => {
        const { name, value } = e.target;
        setHistoryFilters(prev => {
            const newFilters = { ...prev, [name]: value };
            if (name === 'year') newFilters.month = 'all';
            return newFilters;
        });
    };

    const sortItems = (items, sortConfig) => {
        const sortedItems = [...items];
        sortedItems.sort((a, b) => {
            const valA = a[sortConfig.key] || '';
            const valB = b[sortConfig.key] || '';
            if (valA instanceof Date && valB instanceof Date) {
                return sortConfig.direction === 'asc' ? valA.getTime() - valB.getTime() : valB.getTime() - valA.getTime();
            }
            if (typeof valA === 'string') {
                return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            if (typeof valA === 'number') {
                return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
            }
            return 0;
        });
        return sortedItems;
    };
    
    const filteredAndSortedPayables = useMemo(() => {
        let items = purchaseFlow.payables || [];
        if (payablesFilters.search) {
            const searchLower = payablesFilters.search.toLowerCase();
            items = items.filter(r => r.description?.toLowerCase().includes(searchLower));
        }
        if (payablesFilters.year !== 'all') {
            items = items.filter(r => r.dueDate && r.dueDate.getFullYear().toString() === payablesFilters.year.toString());
        }
        if (payablesFilters.month !== 'all') {
            items = items.filter(r => r.dueDate && r.dueDate.getMonth().toString() === payablesFilters.month.toString());
        }
        return sortItems(items, payablesSort);
    }, [purchaseFlow.payables, payablesFilters, payablesSort]);

    const paginatedPayables = useMemo(() => {
        const startIndex = (payablesPage - 1) * payablesItemsPerPage;
        return filteredAndSortedPayables.slice(startIndex, startIndex + payablesItemsPerPage);
    }, [filteredAndSortedPayables, payablesPage, payablesItemsPerPage]);

    const filteredAndSortedHistory = useMemo(() => {
        let items = purchaseFlow.paidHistory || [];
        if (historyFilters.search) {
            const searchLower = historyFilters.search.toLowerCase();
            items = items.filter(r => r.description?.toLowerCase().includes(searchLower));
        }
        if (historyFilters.year !== 'all') {
            items = items.filter(r => r.date && r.date.getFullYear().toString() === historyFilters.year.toString());
        }
        if (historyFilters.month !== 'all') {
            items = items.filter(r => r.date && r.date.getMonth().toString() === historyFilters.month.toString());
        }
        return sortItems(items, historySort);
    }, [purchaseFlow.paidHistory, historyFilters, historySort]);

    const paginatedHistory = useMemo(() => {
        const startIndex = (historyPage - 1) * historyItemsPerPage;
        return filteredAndSortedHistory.slice(startIndex, startIndex + historyItemsPerPage);
    }, [filteredAndSortedHistory, historyPage, historyItemsPerPage]);

    const isAllHistorySelected = useMemo(() => {
        if (paginatedHistory.length === 0) return false;
        return selectedIds && paginatedHistory.every(item => selectedIds.includes(item.id));
    }, [paginatedHistory, selectedIds]);

    const monthlyChartData = useMemo(() => {
        const yearData = purchaseFlow.byYear[selectedYear] || { monthlyToPay: Array(12).fill(0), monthlyPaid: Array(12).fill(0) };
        const data = selectedMetric === 'monthlyToPay' ? yearData.monthlyToPay : yearData.monthlyPaid;
        const label = selectedMetric === 'monthlyToPay' ? 'Previsão de Pagamentos' : 'Pagamentos Realizados';
        const backgroundColor = selectedMetric === 'monthlyToPay' ? 'rgba(239, 68, 68, 0.6)' : 'rgba(249, 115, 22, 0.6)';
        const borderColor = selectedMetric === 'monthlyToPay' ? 'rgba(239, 68, 68, 1)' : 'rgba(249, 115, 22, 1)';
        
        return {
            labels: months.map(m => m.substring(0,3)),
            datasets: [{
                label: `${label} em ${selectedYear}`,
                data: data,
                backgroundColor: backgroundColor,
                borderColor: borderColor,
                borderWidth: 1,
            }]
        };
    }, [selectedYear, selectedMetric, purchaseFlow.byYear]);

    const handleSelectPayable = (uniqueId, defaultAmount) => {
        setSelectedPayables(prev => {
            const newSet = new Set(prev);
            if (newSet.has(uniqueId)) {
                newSet.delete(uniqueId);
                setEditedBulkAmounts(prevAmounts => {
                    const newAmounts = { ...prevAmounts };
                    delete newAmounts[uniqueId];
                    return newAmounts;
                });
            } else {
                newSet.add(uniqueId);
                setEditedBulkAmounts(prevAmounts => ({ ...prevAmounts, [uniqueId]: defaultAmount }));
            }
            return newSet;
        });
    };

    const handleSelectAllPayables = () => {
        if (selectedPayables.size === paginatedPayables.length && paginatedPayables.length > 0) {
            setSelectedPayables(new Set());
            setEditedBulkAmounts({});
        } else {
            const newSet = new Set();
            const newAmounts = { ...editedBulkAmounts };
            paginatedPayables.forEach(item => {
                const uniqueId = `${item.purchaseId}|${item.installmentNumber}`;
                newSet.add(uniqueId);
                newAmounts[uniqueId] = item.amountToPay;
            });
            setSelectedPayables(newSet);
            setEditedBulkAmounts(newAmounts);
        }
    };

    const handleBulkAmountChange = (uniqueId, newValue) => {
        setEditedBulkAmounts(prev => ({
            ...prev,
            [uniqueId]: newValue
        }));
    };

    const totalSelectedValue = useMemo(() => {
        return Object.values(editedBulkAmounts).reduce((sum, val) => sum + Number(val || 0), 0);
    }, [editedBulkAmounts]);

    const executeBulkPayment = async (e) => {
        e.preventDefault();
        const itemsToPay = Array.from(selectedPayables).map(uniqueId => {
            const [purchaseId, installmentNumber] = uniqueId.split('|');
            return {
                purchaseId,
                installmentNumber: Number(installmentNumber),
                amountToPay: Number(editedBulkAmounts[uniqueId])
            };
        });
        
        try {
            await bulkAddPaymentsToPurchases(itemsToPay, new Date(`${bulkPaymentDate}T00:00:00`), bulkPaymentMethod);
            setSelectedPayables(new Set());
            setEditedBulkAmounts({});
            setIsBulkModalOpen(false);
        } catch (error) {
            alert('Erro ao realizar pagamento em lote.');
        }
    };

    const availableYearsOptions = useMemo(() => {
        const years = new Set([...purchaseFlow.availableYears, new Date().getFullYear().toString()]);
        return Array.from(years).sort((a, b) => b - a);
    }, [purchaseFlow.availableYears]);

    return (
        <div className="space-y-6 relative">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-2xl font-bold text-gray-800">Compromissos Financeiros</h2>
                <button onClick={() => openModal('addPurchase')} className="custom-button py-2 px-6 font-semibold rounded-lg shadow-sm">
                    + Registrar Compromisso
                </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl border border-red-200 shadow-sm">
                    <h3 className="text-sm font-medium text-red-600">A Pagar (Este Mês)</h3>
                    <p className="text-3xl font-bold text-gray-800 mt-2">{formatCurrency(purchaseFlow.summary.toPayThisMonth)}</p>
                </div>
                <div className="bg-orange-50 p-6 rounded-xl border border-orange-200 shadow-sm">
                    <h3 className="text-sm font-medium text-orange-700">Pago (Este Mês)</h3>
                    <p className="text-3xl font-bold text-orange-900 mt-2">{formatCurrency(purchaseFlow.summary.paidThisMonth)}</p>
                </div>
            </div>
            
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                    <div className="flex items-center gap-2">
                         <button onClick={() => setSelectedMetric('monthlyToPay')} className={`px-3 py-1 text-sm rounded-lg ${selectedMetric === 'monthlyToPay' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'}`}>A Pagar</button>
                         <button onClick={() => setSelectedMetric('monthlyPaid')} className={`px-3 py-1 text-sm rounded-lg ${selectedMetric === 'monthlyPaid' ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700'}`}>Pago</button>
                    </div>
                    <div className="flex items-center">
                        <label htmlFor="year-select-finance" className="mr-2 text-sm font-medium">Ano:</label>
                        <select 
                            id="year-select-finance" 
                            value={selectedYear} 
                            onChange={(e) => setSelectedYear(e.target.value)} 
                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none"
                        >
                            {availableYearsOptions.map(year => <option key={year} value={year}>{year}</option>)}
                        </select>
                    </div>
                </div>
                 <div className="h-72">
                    <Bar 
                        key={JSON.stringify(monthlyChartData)}
                        data={monthlyChartData} 
                        options={{ responsive: true, maintainAspectRatio: false, scales: { y: { ticks: { callback: value => formatCurrency(value) } } } }}
                    />
                 </div>
            </div>

            {/* SESSÃO: CONTAS A PAGAR */}
            <div ref={tableRef} id="contas-a-pagar" className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                    <h3 className="text-lg font-bold text-gray-800">Contas a Pagar</h3>
                    
                    {selectedPayables.size > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                            <button onClick={() => setIsBulkModalOpen(true)} className="bg-green-600 text-white py-2 px-4 font-semibold rounded-lg shadow hover:bg-green-700 transition-colors">
                                Baixa em Lote ({selectedPayables.size})
                            </button>
                            <button 
                                onClick={() => {
                                    const uniquePurchaseIds = Array.from(new Set(Array.from(selectedPayables).map(id => id.split('|')[0])));
                                    openModal('confirmDelete', { 
                                        type: 'bulkPurchases', 
                                        count: uniquePurchaseIds.length, 
                                        ids: uniquePurchaseIds,
                                        name: 'compras selecionadas'
                                    });
                                }} 
                                className="flex items-center gap-2 bg-red-600 text-white py-2 px-4 font-semibold rounded-lg shadow hover:bg-red-700 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                Excluir Selecionados
                            </button>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <input type="text" name="search" value={payablesFilters.search} onChange={handlePayablesFilterChange} placeholder="🔎 Pesquisar descrição..." className="w-full lg:col-span-1 px-4 py-2 bg-white border border-gray-300 rounded-lg"/>
                    <select name="year" value={payablesFilters.year} onChange={handlePayablesFilterChange} className="w-full lg:col-span-1 px-4 py-2 bg-white border border-gray-300 rounded-lg"><option value="all">Todos os Anos</option>{purchaseFlow.availableYears.map(year => <option key={year} value={year}>{year}</option>)}</select>
                    <select name="month" value={payablesFilters.month} onChange={handlePayablesFilterChange} className="w-full lg:col-span-1 px-4 py-2 bg-white border border-gray-300 rounded-lg" disabled={payablesFilters.year === 'all'}><option value="all">Todos os Meses</option>{months.map((month, index) => <option key={index} value={index}>{month}</option>)}</select>
                </div>

                <div className="space-y-4 md:hidden">
                    <div className="flex items-center gap-2 px-2 pb-2 border-b">
                        <input type="checkbox" checked={paginatedPayables.length > 0 && selectedPayables.size === paginatedPayables.length} onChange={handleSelectAllPayables} className="h-5 w-5 rounded border-gray-300" />
                        <span className="text-sm text-gray-600 font-medium">Selecionar Todos da Página</span>
                    </div>
                    {paginatedPayables.length > 0 ? paginatedPayables.map(item => {
                        const purchase = purchases.find(p => p.id === item.purchaseId);
                        const uniqueId = `${item.purchaseId}|${item.installmentNumber}`;
                        return (
                            <div key={uniqueId} className="border rounded-lg p-4 flex gap-3 items-start">
                                <input type="checkbox" checked={selectedPayables.has(uniqueId)} onChange={() => handleSelectPayable(uniqueId, item.amountToPay)} className="h-5 w-5 rounded border-gray-300 mt-1" />
                                <div className="flex-1 space-y-2">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="font-bold text-gray-800">{item.description}</h3>
                                            <p className="text-sm text-gray-600">Parcela {item.installmentNumber}</p>
                                        </div>
                                        <p className="font-bold text-lg text-red-600 flex-shrink-0 ml-2">{formatCurrency(item.amountToPay)}</p>
                                    </div>
                                    <div className="flex justify-between items-center pt-3 border-t">
                                        <p className="text-sm font-medium text-gray-500">Vence: {item.dueDate ? new Date(item.dueDate).toLocaleDateString('pt-BR') : 'N/A'}</p>
                                        <div className="flex items-center gap-2">
                                            <button className="action-btn text-xl" onClick={() => openModal('addExpensePayment', purchase)} title="Registrar Pagamento">💸</button>
                                            <button className="action-btn text-xl" onClick={() => openModal('editPurchase', purchase)} title="Editar Compra">✏️</button>
                                            <button className="action-btn delete-btn text-xl" onClick={() => openModal('confirmDelete', { id: purchase.id, type: 'purchase', name: item.description })} title="Apagar Compra">🗑️</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }) : (
                        <p className="p-4 text-center text-gray-500">Nenhuma conta a pagar para os filtros selecionados.</p>
                    )}
                </div>

                <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-left">
                        <thead className="border-b-2 border-gray-200">
                            <tr>
                                <th className="p-4 w-12 text-center" title="Selecionar para Baixa em Lote ou Excluir">
                                    <input type="checkbox" checked={paginatedPayables.length > 0 && selectedPayables.size === paginatedPayables.length} onChange={handleSelectAllPayables} className="h-4 w-4 rounded border-gray-300" />
                                </th>
                                <th className="p-4 text-sm font-semibold sortable-header cursor-pointer" onClick={() => handlePayablesSort('dueDate')}>Vencimento</th>
                                <th className="p-4 text-sm font-semibold sortable-header cursor-pointer" onClick={() => handlePayablesSort('description')}>Descrição</th>
                                <th className="p-4 text-sm font-semibold">Parcela</th>
                                <th className="p-4 text-sm font-semibold sortable-header cursor-pointer" onClick={() => handlePayablesSort('amountToPay')}>Valor a Pagar</th>
                                <th className="p-4 text-sm font-semibold text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedPayables.length > 0 ? paginatedPayables.map(item => {
                                const purchase = purchases.find(p => p.id === item.purchaseId);
                                const uniqueId = `${item.purchaseId}|${item.installmentNumber}`;
                                return (
                                    <tr key={uniqueId} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="p-4 text-center">
                                            <input type="checkbox" checked={selectedPayables.has(uniqueId)} onChange={() => handleSelectPayable(uniqueId, item.amountToPay)} className="h-4 w-4 rounded border-gray-300" />
                                        </td>
                                        <td className="p-4 text-gray-600">{item.dueDate ? new Date(item.dueDate).toLocaleDateString('pt-BR') : 'N/A'}</td>
                                        <td className="p-4 font-medium text-gray-800">{item.description}</td>
                                        <td className="p-4 text-gray-600">{item.installmentNumber}</td>
                                        <td className="p-4 font-bold text-red-600">{formatCurrency(item.amountToPay)}</td>
                                        <td className="p-4 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <button className="action-btn text-xl" onClick={() => openModal('addExpensePayment', purchase)} title="Registrar Pagamento">💸</button>
                                                <button className="action-btn text-xl" onClick={() => openModal('editPurchase', purchase)} title="Editar Compra">✏️</button>
                                                <button className="action-btn delete-btn text-xl" onClick={() => openModal('confirmDelete', { id: purchase.id, type: 'purchase', name: item.description })} title="Apagar Compra">🗑️</button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            }) : (
                                <tr>
                                    <td colSpan="6" className="p-4 text-center text-gray-500">Nenhuma conta a pagar para os filtros selecionados.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination totalItems={filteredAndSortedPayables.length} itemsPerPage={payablesItemsPerPage} currentPage={payablesPage} onPageChange={setPayablesPage} onItemsPerPageChange={(val) => { setPayablesItemsPerPage(Number(val)); setPayablesPage(1); }} />
            </div>
            
            {/* SESSÃO: HISTÓRICO DE PAGAMENTOS */}
            <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Histórico de Pagamentos</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <input type="text" name="search" value={historyFilters.search} onChange={handleHistoryFilterChange} placeholder="🔎 Pesquisar descrição..." className="w-full lg:col-span-1 px-4 py-2 bg-white border border-gray-300 rounded-lg"/>
                    <select name="year" value={historyFilters.year} onChange={handleHistoryFilterChange} className="w-full lg:col-span-1 px-4 py-2 bg-white border border-gray-300 rounded-lg"><option value="all">Todos os Anos</option>{purchaseFlow.availableYears.map(year => <option key={year} value={year}>{year}</option>)}</select>
                    <select name="month" value={historyFilters.month} onChange={handleHistoryFilterChange} className="w-full lg:col-span-1 px-4 py-2 bg-white border border-gray-300 rounded-lg" disabled={historyFilters.year === 'all'}><option value="all">Todos os Meses</option>{months.map((month, index) => <option key={index} value={index}>{month}</option>)}</select>
                </div>

                {selectedIds && selectedIds.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center mb-4">
                        <span className="text-sm font-medium text-blue-800">
                            {selectedIds.length} {selectedIds.length > 1 ? 'pagamentos selecionados' : 'pagamento selecionado'}
                        </span>
                        <button onClick={onDeleteSelected} className="flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-800">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Excluir Selecionados
                        </button>
                    </div>
                )}
                
                <div className="space-y-4 md:hidden">
                    <div className="flex items-center gap-2 px-2 pb-2 border-b">
                        <input type="checkbox" checked={isAllHistorySelected} onChange={() => onSelectAll(paginatedHistory.map(item => item.id), isAllHistorySelected)} className="h-5 w-5 rounded border-gray-300" />
                        <span className="text-sm text-gray-600 font-medium">Selecionar Todos (Excluir)</span>
                    </div>

                    {paginatedHistory.length > 0 ? paginatedHistory.map((item) => {
                        return (
                            <div key={item.id} className="border rounded-lg p-4 space-y-3">
                                <div className="flex justify-between items-start">
                                    <div className="flex items-center gap-2">
                                        <input type="checkbox" className="h-4 w-4 rounded border-gray-300 mt-1" checked={selectedIds?.includes(item.id)} onChange={() => onSelectOne(item.id)} />
                                        <div>
                                            <h3 className="font-bold text-gray-800">{item.description}</h3>
                                            <p className="text-sm text-gray-600">Via {item.method}</p>
                                        </div>
                                    </div>
                                    <p className="font-bold text-lg text-orange-600 flex-shrink-0 ml-2">{formatCurrency(item.amount)}</p>
                                </div>
                                <div className="flex justify-between items-center pt-3 border-t">
                                    <p className="text-sm font-medium text-gray-500">Pago em: {item.date ? new Date(item.date).toLocaleDateString('pt-BR') : 'N/A'}</p>
                                    <div className="flex items-center gap-2">
                                        <button className="action-btn text-xl" onClick={() => openModal('editExpensePayment', item)} title="Editar Pagamento">✏️</button>
                                        <button className="action-btn delete-btn text-xl" onClick={() => openModal('confirmDelete', { id: item.id, purchaseId: item.purchaseId, type: 'expensePayment', name: item.description })} title="Apagar Pagamento">🗑️</button>
                                    </div>
                                </div>
                            </div>
                        )
                    }) : <p className="p-4 text-center text-gray-500">Nenhum pagamento registrado para os filtros selecionados.</p>}
                </div>

                <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-left">
                        <thead className="border-b-2 border-gray-200">
                            <tr>
                                <th className="p-4 w-4">
                                    <input type="checkbox" className="h-4 w-4 rounded border-gray-300" checked={isAllHistorySelected} onChange={() => onSelectAll(paginatedHistory.map(item => item.id), isAllHistorySelected)} />
                                </th>
                                <th className="p-4 text-sm font-semibold sortable-header cursor-pointer" onClick={() => handleHistorySort('date')}>Data do Pagamento</th>
                                <th className="p-4 text-sm font-semibold sortable-header cursor-pointer" onClick={() => handleHistorySort('description')}>Descrição</th>
                                <th className="p-4 text-sm font-semibold">Método</th>
                                <th className="p-4 text-sm font-semibold sortable-header cursor-pointer" onClick={() => handleHistorySort('amount')}>Valor Pago</th>
                                <th className="p-4 text-sm font-semibold text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedHistory.length > 0 ? paginatedHistory.map((item) => {
                                return (
                                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="p-4">
                                            <input type="checkbox" className="h-4 w-4 rounded border-gray-300" checked={selectedIds?.includes(item.id)} onChange={() => onSelectOne(item.id)} />
                                        </td>
                                        <td className="p-4 text-gray-600">{item.date ? new Date(item.date).toLocaleDateString('pt-BR') : 'N/A'}</td>
                                        <td className="p-4 font-medium text-gray-800">{item.description}</td>
                                        <td className="p-4 text-gray-600">{item.method}</td>
                                        <td className="p-4 font-bold text-orange-600">{formatCurrency(item.amount)}</td>
                                        <td className="p-4 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <button className="action-btn text-xl" onClick={() => openModal('editExpensePayment', item)} title="Editar Pagamento">✏️</button>
                                                <button className="action-btn delete-btn text-xl" onClick={() => openModal('confirmDelete', { id: item.id, purchaseId: item.purchaseId, type: 'expensePayment', name: item.description })} title="Apagar Pagamento">🗑️</button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            }) : <tr><td colSpan="6" className="p-4 text-center text-gray-500">Nenhum pagamento registrado para os filtros selecionados.</td></tr>}
                        </tbody>
                    </table>
                </div>
                <Pagination totalItems={filteredAndSortedHistory.length} itemsPerPage={historyItemsPerPage} currentPage={historyPage} onPageChange={setHistoryPage} onItemsPerPageChange={(val) => { setHistoryItemsPerPage(Number(val)); setHistoryPage(1); }} />
            </div>

            {/* MODAL DE PAGAMENTO EM LOTE COM BLINDAGEM */}
            {isBulkModalOpen && (
                <div className="modal-overlay z-50 flex items-center justify-center fixed inset-0 bg-black bg-opacity-50">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-xl m-4 max-h-[90vh] flex flex-col">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Baixa em Lote</h2>
                        <p className="text-gray-600 mb-4">Ajuste os valores reais cobrados antes de confirmar.</p>
                        
                        <div className="flex-1 overflow-y-auto pr-2 mb-4 space-y-3">
                            {Array.from(selectedPayables).map(uniqueId => {
                                const [purchaseId, instNum] = uniqueId.split('|');
                                const item = purchaseFlow.payables.find(p => p.purchaseId === purchaseId && String(p.installmentNumber) === instNum);
                                return (
                                    <div key={uniqueId} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg gap-2">
                                        <div className="flex-1">
                                            <p className="font-semibold text-gray-800 text-sm truncate">{item?.description}</p>
                                            <p className="text-xs text-gray-500">Parcela {instNum} - Vence: {item?.dueDate ? new Date(item.dueDate).toLocaleDateString('pt-BR') : ''}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-600">R$</span>
                                            {/* BLINDAGEM DE INPUT: Não permite valor maior que o da parcela, nem negativo */}
                                            <input 
                                                type="number" 
                                                step="0.01" 
                                                value={editedBulkAmounts[uniqueId] !== undefined ? editedBulkAmounts[uniqueId] : ''} 
                                                onChange={(e) => {
                                                    let val = e.target.value;
                                                    if (val !== '') {
                                                        let numVal = Number(val);
                                                        if (numVal > item?.amountToPay) val = item?.amountToPay;
                                                        if (numVal < 0) val = 0;
                                                    }
                                                    handleBulkAmountChange(uniqueId, val);
                                                }}
                                                className="w-28 px-3 py-1 border border-gray-300 rounded-lg text-right font-medium focus:ring-[#D7BFBD] focus:border-[#D7BFBD]"
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        <div className="bg-red-50 p-4 rounded-lg mb-4 border border-red-100 flex justify-between items-center">
                            <span className="font-medium text-red-800">Total a Pagar:</span>
                            <span className="text-2xl font-bold text-red-600">{formatCurrency(totalSelectedValue)}</span>
                        </div>

                        <form onSubmit={executeBulkPayment} className="space-y-4 pt-2 border-t">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="form-label">Data do Pagamento</label>
                                    <input type="date" value={bulkPaymentDate} onChange={e => setBulkPaymentDate(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg" required />
                                </div>
                                <div>
                                    <label className="form-label">Forma de Pagamento</label>
                                    <select value={bulkPaymentMethod} onChange={e => setBulkPaymentMethod(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                                        <option value="Pix">Pix</option>
                                        <option value="Cartão de Crédito">Cartão de Crédito</option>
                                        <option value="Boleto">Boleto</option>
                                        <option value="Transferência">Transferência</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex justify-end gap-4 pt-4">
                                <button type="button" onClick={() => setIsBulkModalOpen(false)} className="py-2 px-6 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300">Cancelar</button>
                                <button type="submit" disabled={totalSelectedValue <= 0} className="bg-green-600 text-white py-2 px-6 font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50">Confirmar Pagamento</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompromissosView;